import React, { useState } from 'react';
import { Table } from 'reactstrap';
import PropTypes, { any } from 'prop-types';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import { partial } from '../../../util/translation/translation';
import FormCard from '../../../shared/components/custom/FormCard';

const s = partial('shared');
const r = partial('UserRoles');

const FILTERS = ['all', 'on', 'off'];

const PermissionForm = ({ permissions, handleToggle, loading }) => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const enabled = permissions.filter((permission) => permission.toggled);
  const counts = {
    all: permissions.length,
    on: enabled.length,
    off: permissions.length - enabled.length,
  };
  const filterLabel = {
    all: s('policiesAll'),
    on: s('policiesOn'),
    off: s('policiesOff'),
  };

  // Keep each policy's index in `permissions`, which handleToggle expects
  const query = search.trim().toLowerCase();
  const rows = permissions
    .map((permission, index) => ({ permission, index }))
    .filter(({ permission }) => {
      if (filter === 'on' && !permission.toggled) return false;
      if (filter === 'off' && permission.toggled) return false;
      if (!query) return true;
      return `${r(permission.name)} ${r(`${permission.name}Description`)}`
        .toLowerCase()
        .includes(query);
    });

  return (
    <FormCard
      title={
        <>
          {s('policies')}
          <span className="form-card__count">
            {enabled.length} / {permissions.length}
          </span>
        </>
      }
      actions={
        <div className="policy-filters">
          {FILTERS.map((key) => (
            <button
              key={key}
              type="button"
              className={`stats-range${filter === key ? ' active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {filterLabel[key]} {counts[key]}
            </button>
          ))}
        </div>
      }
      bodyClassName="form-card__body--flush"
    >
      <div className="policy-toolbar">
        <p className="policy-toolbar__hint">{s('policiesHint')}</p>
        <label className="policy-search">
          <MagnifyIcon size={18} />
          <input
            type="search"
            value={search}
            placeholder={s('searchPolicies')}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>
      <Table
        responsive
        className={`table--roomy table--stack policy-table mb-0${
          loading ? ' policy-table--busy' : ''
        }`}
      >
        <thead>
          <tr>
            <th>{s('role')}</th>
            <th>{s('description')}</th>
            <th className="policy-table__switch-col" />
          </tr>
        </thead>
        <tbody>
          {rows.map(({ permission, index }) => (
            <tr
              key={`permission-${permission.id}`}
              className={permission.toggled ? 'policy-table__row--on' : ''}
              onClick={() => !loading && handleToggle(index)}
            >
              <td className="table-stack__primary">
                <span
                  className={`policy-dot${
                    permission.toggled ? ' policy-dot--on' : ''
                  }`}
                />
                <strong className="font-weight-500">
                  {r(permission.name)}
                </strong>
              </td>
              <td data-label={s('description')} className="text-muted">
                {r(`${permission.name}Description`)}
              </td>
              <td className="table-stack__actions policy-table__switch-col">
                {/* The row handles the click; the switch only shows state */}
                <div className="switch-btn">
                  <label
                    className={`switch-btn--container${
                      permission.toggled ? ' active' : ''
                    }`}
                  >
                    <input
                      className="switch-btn--input"
                      type="checkbox"
                      aria-label={r(permission.name)}
                      checked={!!permission.toggled}
                      disabled={loading}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggle(index)}
                    />
                    <span className="switch-btn--button" />
                  </label>
                </div>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={3} className="policy-table__empty">
                {s('noPoliciesMatch')}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </FormCard>
  );
};

PermissionForm.propTypes = {
  permissions: PropTypes.arrayOf(any).isRequired,
  handleToggle: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

PermissionForm.defaultProps = {
  loading: false,
};

export default PermissionForm;
